/**
 * Round-end confetti: a transparent fixed canvas where flat paper rects burst
 * from both screen edges and tumble across/down. Self-stops when all pieces
 * leave the screen; zero cost while idle (canvas removed from the DOM).
 */

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
  w: number;
  h: number;
  color: string;
  wobble: number;
}

const COLORS = ["#e23b30", "#2f9d22", "#1d6fd1", "#ffd23c", "#9b59ff", "#ff7733", "#3ad6c0", "#ffffff"];

export class Confetti {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private pieces: Piece[] = [];
  private raf = 0;
  private lastT = 0;

  /** Fire a celebration burst across the whole screen. */
  burst(count = 140) {
    const w = window.innerWidth;
    for (let i = 0; i < count; i++) {
      const fromLeft = i % 2 === 0;
      this.pieces.push({
        // launch from the two top corners, arcing across the screen
        x: fromLeft ? -10 : w + 10,
        y: window.innerHeight * (0.05 + Math.random() * 0.25),
        vx: (fromLeft ? 1 : -1) * (250 + Math.random() * 650),
        vy: -(150 + Math.random() * 350),
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 14,
        w: 7 + Math.random() * 7,
        h: 5 + Math.random() * 5,
        color: COLORS[(Math.random() * COLORS.length) | 0],
        wobble: Math.random() * Math.PI * 2,
      });
    }
    this.ensureRunning();
  }

  private ensureRunning() {
    if (this.canvas) return;
    const c = document.createElement("canvas");
    c.width = window.innerWidth;
    c.height = window.innerHeight;
    c.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:35;";
    document.body.appendChild(c);
    this.canvas = c;
    this.ctx = c.getContext("2d");
    this.lastT = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  private tick = () => {
    const now = performance.now();
    const dt = Math.min(0.05, (now - this.lastT) / 1000);
    this.lastT = now;
    const ctx = this.ctx;
    const c = this.canvas;
    if (!ctx || !c) return;
    ctx.clearRect(0, 0, c.width, c.height);
    const H = c.height;
    for (const p of this.pieces) {
      p.vy += 620 * dt; // gravity
      p.vx *= 1 - 0.7 * dt; // drag
      p.wobble += dt * 7;
      p.x += (p.vx + Math.sin(p.wobble) * 40) * dt;
      p.y += p.vy * dt;
      p.rot += p.vrot * dt;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      // fake 3D flutter by squashing on the wobble
      ctx.scale(1, 0.4 + 0.6 * Math.abs(Math.sin(p.wobble)));
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    }
    this.pieces = this.pieces.filter((p) => p.y < H + 30);
    if (this.pieces.length) {
      this.raf = requestAnimationFrame(this.tick);
    } else {
      this.stop();
    }
  };

  private stop() {
    cancelAnimationFrame(this.raf);
    this.canvas?.remove();
    this.canvas = null;
    this.ctx = null;
  }
}
